import { usePermissions } from 'context/PermissionsContext';
import { PermissionKey } from 'types/rbac';

export interface ProtectedResourceProps {
  permission?: PermissionKey | PermissionKey[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * A component to protect routes or resources based on user roles or permissions.
 *
 * If the user has access to the given roles or permissions, it renders the children.
 * Otherwise, it renders the given fallback component.
 *
 * @permissions {PermissionKey[]} - An array of permissions that are required to access the protected resource
 * @param fallback {React.ReactNode} - A fallback component to render when the user does not have access
 * @param children {React.ReactNode} - The protected resource to render
 *
 * @return {React.ReactNode} The protected resource if the user has access, otherwise the fallback
 */
const ProtectedResource = ({
  permission,
  fallback = null,
  children,
}: ProtectedResourceProps) => {
  const { checkPermissions, isLoading } = usePermissions();

  // Stop checking permissions if loading
  if (isLoading) return null;

  const hasAccess = checkPermissions(permission);

  if (!hasAccess) return fallback;

  return children;
};
export default ProtectedResource;
